import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA, MatLegacyDialogRef } from '@angular/material/legacy-dialog';
import { DataRole, ExprForm } from '@mode-switch/express';
import { Query, QueryFrame } from '@mode-switch/query';
import { BasicChannel, Encoding, ExprFrame } from '@mode-vegas/vegas';
import { trackByFactory } from '@mode/capra';
import { VegasFrame } from '@mode/charts/util-vegas';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { HelpDocsService } from '@mode/shared/util-js';
import { CompiledField, FormulaOperations } from '@mode/shared/util-switch';
import { uniqBy } from 'lodash';

import {
  FrameTypeOpt,
  OutOfScopeField,
  QuickCalcOpt,
  RelativeToOpt,
  TotalUsingOpt,
  asserts,
  frameTypeOptions,
  getBasicPositionalChannel,
  getFormulasWithSortWarnings,
  getOutOfScopeAddrFields,
  makeEncodingForField,
  makePivotQuery,
  quickCalcOptions,
  relativeToOptions,
  totalUsingOptions,
} from './table-calc-dialog.helpers';

export interface TableCalcDialogData {
  view: FlamingoTypes.ModeView;
  encoding: Encoding;
  field: CompiledField;
  onApply(field: CompiledField): void;
}

@Component({
  selector: 'mode-table-calc-dialog',
  templateUrl: './table-calc-dialog.component.html',
  styleUrls: ['./table-calc-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableCalcDialogComponent {
  // Menu item selection
  quickCalc?: QuickCalcOpt;
  relativeTo?: RelativeToOpt;
  totalUsing?: TotalUsingOpt;
  frameType?: FrameTypeOpt;

  // Menus
  quickCalcOptions = quickCalcOptions;
  frameTypeOptions = frameTypeOptions;
  relativeToOptions = relativeToOptions;
  totalUsingOptions = totalUsingOptions;

  // Addressing/partitioning field sets
  addrFields: CompiledField[] = [];
  partFields: CompiledField[] = [];

  // Drag stuff
  sourceList: CompiledField[] = [];
  dragField?: CompiledField;

  /**
   * View for field compilation
   */
  view: FlamingoTypes.ModeView;

  /**
   * Encoding within which the field resides
   */
  encoding: Encoding;

  /**
   * If the field is not a mark then this will have a value. Used to
   * inform the creation of a default frame if the field requires one
   */
  channel?: BasicChannel;

  /**
   * The encoding as a pivot query. With this, we may leverage the APIs
   * in switch/query for extracting addressing and partitioning fields
   */
  query: Query.Pivot<DataRole>;

  /**
   * Persisted, restorable configuration of the field (allows for "reset")
   */
  prevField: CompiledField;

  /**
   * Working copy of the field. Holds the current set of changes
   */
  field: CompiledField;

  /**
   * Formulas of the addressing set that do not exist in scope of the field
   */
  outOfScopeAddrFields: OutOfScopeField[] = [];

  /**
   * A set of formulas to display sort warnings/infos for; see
   * {@link getFormulasWithSortWarnings} for details on this
   */
  formulasWithSortWarnings: Set<string>;

  /**
   * Displayed in a tooltip for dimensions of the partitioning set
   * that have their formula in {@link formulasWithSortWarnings}
   */
  sortWarningTooltipContent = 'This field’s nested sort will no longer be applied to your visualization.';

  /**
   * ngFor trackBy function for identifying a field by alias
   */
  trackByAlias = trackByFactory<{ alias: string }>(({ alias }) => alias);

  constructor(
    protected dialogRef: MatLegacyDialogRef<TableCalcDialogComponent>,
    @Inject(MAT_LEGACY_DIALOG_DATA) protected data: TableCalcDialogData,
    public helpDocs: HelpDocsService
  ) {
    asserts(data.field.alias, `init -> the provided field must have an alias`);

    this.view = data.view;
    this.encoding = makeEncodingForField(data.encoding, data.field.alias);
    this.channel = getBasicPositionalChannel(this.encoding, data.field.alias);
    this.query = makePivotQuery(data.view, this.encoding);
    this.formulasWithSortWarnings = getFormulasWithSortWarnings(this.query);
    this.prevField = data.field;
    this.field = data.field;

    // MUST be analytic
    if (!this.field.isAnalyticCalc) {
      const formula = FormulaOperations.diff(this.field);
      this.field = new CompiledField(this.view, formula, this.field.alias, this.field.varType);
    }

    // MUST have a frame
    if (!this.field.frame) {
      const frame = VegasFrame.makeDefaultExprFrame(this.encoding, this.field, this.channel);
      this.field = new CompiledField(this.view, this.field.formula, this.field.alias, this.field.varType, frame);
    }

    // Sync up the menus and such
    this.sync();
  }

  /**
   * Ensures the appropriate menu options and addressing/partitioning
   * dimensions are set per the current field configuration
   */
  sync(): void {
    const frame = this.field.frame;
    asserts(frame, 'sync -> field must have a frame');

    // Selected calc (optional)
    const quickCalc = this.field.form;
    this.quickCalc = this.quickCalcOptions.find((opt) => opt.value === quickCalc);

    // Relative to (optional)
    const relativeTo = this.field.relativeTo;
    this.relativeTo = this.relativeToOptions.find((opt) => opt.value === relativeTo);

    // Total using (optional)
    const totalUsing = this.field.totalUsing;
    this.totalUsing = this.totalUsingOptions.find((opt) => opt.value === totalUsing);

    // Selected frame (required)
    const frameType = VegasFrame.fromExprFrame(frame);
    this.frameType = this.frameTypeOptions.find((opt) => opt.value === frameType);
    asserts(this.frameType, `sync -> could not find menu option for frame ${frame.type}`);

    // Collect invalid custom addressing fields, if any
    this.outOfScopeAddrFields = [];
    if (frame.type === ExprFrame.Type.Custom) {
      const fields = getOutOfScopeAddrFields(this.view, this.encoding, frame.address);
      this.outOfScopeAddrFields = fields;
    }

    // Set the addressing/partitioning fields. De-duplicate on formula
    // since we don't want to see multiple occurrences of the same
    // dimension by formula
    this.addrFields = uniqBy(
      QueryFrame.getAddressingDimensions(this.query, frame).map((field) => {
        return CompiledField.fromField(this.view, field);
      }),
      'formula'
    );
    this.partFields = uniqBy(
      QueryFrame.getPartitioningDimensions(this.query, frame).map((field) => {
        return CompiledField.fromField(this.view, field);
      }),
      'formula'
    );
  }

  /**
   * Returns true if either the addressing or partitioning
   * set of dimensions contains fields, false otherwise
   */
  get hasDimensions(): boolean {
    return this.addrFields.length > 0 || this.partFields.length > 0;
  }

  /**
   * Returns true if the initial field did not have a frame or if
   * the field's configuration has not changed, false otherwise
   */
  get isResetDisabled(): boolean {
    return (
      this.prevField.frame === undefined ||
      (this.prevField.form === this.field.form &&
        this.prevField.formula === this.field.formula &&
        this.prevField.relativeTo === this.field.relativeTo &&
        this.prevField.totalUsing === this.field.totalUsing &&
        ExprFrame.equals(this.prevField.frame, this.field.frame))
    );
  }

  /**
   * Returns true if the field formula exists in the sort warning set
   * and a custom frame is applied, false otherwise
   */
  hasSortWarning(field: CompiledField): boolean {
    if (this.frameType?.value === VegasFrame.Type.Custom) {
      return this.formulasWithSortWarnings.has(field.formula);
    }
    return false;
  }

  /**
   * Handle Quick Calc changes (Diff, Pct. Diff, etc..)
   */
  onChangeQuickCalc = (item: QuickCalcOpt): void => {
    this.quickCalc = item;

    const field = this.field;
    switch (item.value) {
      case ExprForm.Difference: {
        this.field = field.withNewFormula(FormulaOperations.diff(field));
        break;
      }
      case ExprForm.PctDifference: {
        this.field = field.withNewFormula(FormulaOperations.pctDiff(field));
        break;
      }
      case ExprForm.PctOfTotal: {
        this.field = field.withNewFormula(FormulaOperations.pctOfTotal(field));
        break;
      }
    }

    this.sync();
  };

  /**
   * Handle RelativeTo changes (Prev, Last, etc..)
   */
  onChangeRelTo = (item: RelativeToOpt): void => {
    this.relativeTo = item;

    const field = this.field;
    switch (field.form) {
      case ExprForm.Difference: {
        this.field = field.withNewFormula(FormulaOperations.diff(field, undefined, item.value));
        break;
      }
      case ExprForm.PctDifference: {
        this.field = field.withNewFormula(FormulaOperations.pctDiff(field, undefined, item.value));
        break;
      }
    }

    this.sync();
  };

  /**
   * Handle TotalUsing changes (Total, Avg, etc..)
   */
  onChangeTotalUsing = (item: TotalUsingOpt): void => {
    this.totalUsing = item;

    const field = this.field;
    if (field.form === ExprForm.PctOfTotal) {
      this.field = field.withNewFormula(FormulaOperations.pctOfTotal(field, undefined, item.value));
    }

    this.sync();
  };

  /**
   * Handle frame type changes (PaneAcross, Custom, etc..)
   */
  onChangeFrameType = (item: FrameTypeOpt): void => {
    this.frameType = item;

    const field = this.field;
    const isCustomFrame = item.value === VegasFrame.Type.Custom;
    this.field = new CompiledField(
      this.view,
      field.formula,
      field.alias,
      field.varType,
      VegasFrame.makeExprFrame(
        item.value,
        isCustomFrame
          ? // Prepend remaining out of scope formulas, if any. Each formula must
            // be dealt with either by individual removal or by changing the frame
            // to something other than custom
            [this.outOfScopeAddrFields, this.addrFields].flat().map((f) => f.formula)
          : undefined
      )
    );

    this.sync();
  };

  /**
   * Remove an entry from {@link outOfScopeAddrFields} by index then recompute the frame
   */
  removeOutOfScopeField(index: number): void {
    this.outOfScopeAddrFields.splice(index, 1);
    asserts(this.frameType, `removeOutOfScopeField -> frameType is undefined`);
    this.onChangeFrameType(this.frameType);
  }

  /**
   * Set the drag field and source list at the beginning of the drag
   */
  onDragStart(dragField: CompiledField, sourceList: CompiledField[]): void {
    this.dragField = dragField;
    this.sourceList = sourceList;
  }

  /**
   * Clear the drag field and source list at the end of the drag
   */
  onDragEnd(): void {
    this.dragField = undefined;
    this.sourceList = [];
  }

  /**
   * Handle addressing/partitioning dimension moves -> drops. If offset is true,
   * shift the drop index by the number of displayed (all) out of scope fields;
   * these are rendered at the top of the addressing field list and affect the
   * drop index
   */
  onDrop(index: number | undefined, target: CompiledField[], offset = false): void {
    if (this.dragField === undefined) return;

    const source = this.sourceList;
    const isSameList = source === target;

    let targetIndex = target.length;
    if (typeof index === 'number') {
      const offsetBy = this.outOfScopeAddrFields.length;
      targetIndex = Math.max(0, index - (offset ? offsetBy : 0));
    }

    // Adjusted for drops within the same list
    let sourceIndex = source.indexOf(this.dragField);
    if (isSameList && sourceIndex > targetIndex) {
      sourceIndex += 1;
    }

    // Add to target
    target.splice(targetIndex, 0, this.dragField);

    // Remove from source
    source.splice(sourceIndex, 1);

    // Switch frame type to custom
    const custom = this.frameTypeOptions.find((opt) => opt.value === VegasFrame.Type.Custom);
    asserts(custom, 'onDrop -> expected Custom menu option');
    this.onChangeFrameType(custom);
  }

  /**
   * Replace the current field with the previously applied field and re-sync
   */
  reset(): void {
    this.field = this.prevField;
    this.sync();
  }

  /**
   * Invoke `onApply` with the current field. If `closeAfterApply` is true the dialog
   * will be closed after invoking the callback
   */
  apply(closeAfterApply: boolean): void {
    this.prevField = this.field;

    const { onApply } = this.data;
    onApply(this.field);

    if (closeAfterApply) {
      this.close();
    }
  }

  /**
   * Close the dialog
   */
  close(): void {
    this.dialogRef.close();
  }
}
